import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export type Language = 'pt' | 'en';

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateProfile } = useAuth();

  const currentLanguage = (i18n.language?.substring(0, 2) || 'pt') as Language;

  const getLocalizedPathForLang = useCallback((path: string, lang: Language): string => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;

    // Remove existing language prefix if present
    let basePath = cleanPath;
    if (basePath.startsWith('/en/')) {
      basePath = basePath.substring(3);
    } else if (basePath === '/en') {
      basePath = '/';
    }

    // Add language prefix for English
    if (lang === 'en') {
      return `/en${basePath}`;
    }
    return basePath;
  }, []);

  // Convenience function that uses current language
  const getLocalizedPath = useCallback((path: string): string => {
    return getLocalizedPathForLang(path, currentLanguage);
  }, [getLocalizedPathForLang, currentLanguage]);

  const removeLanguagePrefix = useCallback((path: string): string => {
    if (path.startsWith('/en/')) {
      return path.substring(3);
    }
    if (path === '/en') {
      return '/';
    }
    return path;
  }, []);

  const switchLanguage = useCallback(async (lang: Language) => {
    // Get the current path without language prefix
    const basePath = removeLanguagePrefix(location.pathname);

    // Build the new path with the new language
    const newPath = getLocalizedPathForLang(basePath, lang);

    // Change i18n language
    await i18n.changeLanguage(lang);

    // Store in localStorage
    localStorage.setItem('i18nextLng', lang);

    // Update user preference in backend if authenticated
    if (user) {
      try {
        await updateProfile({ language: lang });
      } catch (error) {
        console.error('Failed to update language preference:', error);
      }
    }

    // Navigate to the new path
    navigate(newPath + location.search + location.hash, { replace: true });
  }, [i18n, navigate, location, user, updateProfile, getLocalizedPathForLang, removeLanguagePrefix]);

  const isEnglish = currentLanguage === 'en';
  const isPortuguese = currentLanguage === 'pt';

  return {
    currentLanguage,
    switchLanguage,
    getLocalizedPath,
    getLocalizedPathForLang,
    removeLanguagePrefix,
    isEnglish,
    isPortuguese,
  };
};

export default useLanguage;
